import { useId, useState } from 'react';
import { RevertIcon } from '../../../theme/icons/RevertIcon';
import { BarcodeViewIcon } from '../../../theme/icons/BarcodeViewIcon';
import {
  Box,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { useAppDispatch, useAppSelector } from '../../../store/redux/hooks';
import { setPreviewLanguage } from '../../../store/redux/slices/template.slice';
import { getLocaleDisplayName } from '../../../constants/locales';
import CustomSpinIcon from '../../../theme/icons/CustomSpinIcon';
import { CustomEarthIcon } from '../../../theme/icons/CustomEarthIcon';
import CustomDevModeIcon from '../../../theme/icons/CustomDevModeIcon';
import { RIGHT_OF_PREV_RAIL_CENTRE } from './utils/previewLayout';

const TOOLTIP_PROPS = {
  placement: 'left' as const,
  openDelay: 500,
  hasArrow: true,
  fontSize: '12px',
  borderRadius: '6px',
  bg: 'secondaryDark',
  color: 'white',
  p: '8px',
};

const HOVER_GRADIENT = {
  bg: 'linear-gradient(180deg, rgba(255, 149, 84, 0.8) 16.5%, rgba(255, 73, 53, 0.8) 100%)',
  bgClip: 'text',
  color: 'transparent',
};

const SELECTED_GRADIENT = {
  bg: 'linear-gradient(180deg, #FF9554 16.5%, #FF4935 100%)',
  bgClip: 'text',
  color: 'transparent',
};

interface PreviewActionsPillProps {
  isDevMode: boolean;
  setIsDevMode: (value: boolean) => void;
  handleFlipPreview: () => void;
  hideFlip?: boolean;
  showRevertButton?: boolean;
  onRevertPreview?: () => void;
  isRevertedPreview?: boolean;
  revertLabel?: string;
  showBarcodeButton?: boolean;
  onBarcodeView?: () => void;
  isBarcodeView?: boolean;
  isPassPreview: boolean;
  addedLanguages: string[];
  passLanguages?: string[];
  passDefaultLanguage?: string;
  selectedPassLanguage?: string | null;
  onPassLanguageChange?: (lang: string | null) => void;
  revertButtonRef?: React.RefObject<HTMLElement>;
  barcodeButtonRef?: React.RefObject<HTMLElement>;
}

const PreviewActionsPill = ({
  isDevMode,
  setIsDevMode,
  handleFlipPreview,
  hideFlip = false,
  showRevertButton = false,
  onRevertPreview,
  isRevertedPreview = false,
  revertLabel,
  showBarcodeButton = false,
  onBarcodeView,
  isBarcodeView = false,
  isPassPreview,
  addedLanguages,
  passLanguages,
  passDefaultLanguage,
  selectedPassLanguage,
  onPassLanguageChange,
  revertButtonRef,
  barcodeButtonRef,
}: PreviewActionsPillProps) => {
  const dispatch = useAppDispatch();
  const previewLanguage = useAppSelector((s) => s.template.previewLanguage);
  const defaultLanguage =
    useAppSelector((s) => s.template.defaultLanguage) || 'en';
  const [hoveredAction, setHoveredAction] = useState<string | null>(null);
  const resolvedRevertLabel =
    revertLabel ?? (isRevertedPreview ? 'New view' : 'Classic view');
  const gradientId = useId();
  const revertGradientId = `revert${gradientId}`;
  const barcodeGradientId = `barcode${gradientId}`;

  const hasLanguages =
    (!isPassPreview && addedLanguages.length > 0) ||
    (isPassPreview && passLanguages && passLanguages.length > 0);

  const isNonDefaultLanguageSelected = isPassPreview
    ? selectedPassLanguage
    : previewLanguage;

  const langTextProps = isNonDefaultLanguageSelected
    ? SELECTED_GRADIENT
    : hoveredAction === 'lang' && !isDevMode
      ? HOVER_GRADIENT
      : { color: '#8E8E8E' };

  return (
    <Flex
      position="absolute"
      left={RIGHT_OF_PREV_RAIL_CENTRE}
      top="50%"
      transform="translate(-50%, -50%)"
      direction="column"
      align="center"
      gap={3}
      bg="white"
      px={2}
      py={4}
      borderRadius="16px"
      boxShadow="0px 4px 12px rgba(0, 0, 0, 0.2)"
      zIndex={2}
      w="44px"
    >
      {showRevertButton && (
        <Tooltip
          label={resolvedRevertLabel}
          {...TOOLTIP_PROPS}
        >
          <IconButton
            ref={revertButtonRef as React.RefObject<HTMLButtonElement>}
            aria-label={resolvedRevertLabel}
            variant="ghost"
            size="xs"
            onClick={onRevertPreview}
            isDisabled={isDevMode}
            _hover={{ bg: 'transparent' }}
            _active={{ bg: 'transparent' }}
            _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
            onMouseEnter={() => setHoveredAction('revert')}
            onMouseLeave={() => setHoveredAction(null)}
          >
            <RevertIcon
              gradientId={revertGradientId}
              fill={
                (hoveredAction === 'revert' && !isDevMode) || isRevertedPreview
                  ? `url(#${revertGradientId})`
                  : '#8E8E8E'
              }
              stroke={
                (hoveredAction === 'revert' && !isDevMode) || isRevertedPreview
                  ? `url(#${revertGradientId})`
                  : '#8E8E8E'
              }
              opacity={
                hoveredAction === 'revert' && !isRevertedPreview ? 0.8 : 1
              }
            />
          </IconButton>
        </Tooltip>
      )}

      {showBarcodeButton && !isRevertedPreview && (
        <>
          <Box w="24px" h="1px" bg="lightGrey" opacity={0.7} />
          <Tooltip label="Barcode view" {...TOOLTIP_PROPS}>
            <IconButton
              ref={barcodeButtonRef as React.RefObject<HTMLButtonElement>}
              aria-label="Barcode view"
              variant="ghost"
              size="xs"
              onClick={onBarcodeView}
              isDisabled={isDevMode}
              _hover={{ bg: 'transparent' }}
              _active={{ bg: 'transparent' }}
              _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
              onMouseEnter={() => setHoveredAction('barcode')}
              onMouseLeave={() => setHoveredAction(null)}
            >
              <BarcodeViewIcon
                gradientId={barcodeGradientId}
                fill={
                  (hoveredAction === 'barcode' && !isDevMode) || isBarcodeView
                    ? `url(#${barcodeGradientId})`
                    : '#8E8E8E'
                }
              />
            </IconButton>
          </Tooltip>
        </>
      )}

      {showRevertButton && !hideFlip && (
        <Box w="24px" h="1px" bg="lightGrey" opacity={0.7} />
      )}

      {!hideFlip && (
        <Tooltip label="Flip preview" {...TOOLTIP_PROPS}>
          <IconButton
            aria-label="Flip preview"
            variant="ghost"
            size="xs"
            onClick={handleFlipPreview}
            isDisabled={isDevMode}
            _hover={{ bg: 'transparent' }}
            _active={{ bg: 'transparent' }}
            _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
            onMouseEnter={() => setHoveredAction('flip')}
            onMouseLeave={() => setHoveredAction(null)}
          >
            <CustomSpinIcon
              boxSize="28px"
              color="#8E8E8E"
              gradient={
                hoveredAction === 'flip' && !isDevMode ? 'hover' : undefined
              }
            />
          </IconButton>
        </Tooltip>
      )}

      {hasLanguages ? (
        <>
          <Box w="24px" h="1px" bg="lightGrey" opacity={0.7} />
          <Menu>
            <Tooltip label="Preview language" {...TOOLTIP_PROPS}>
              <MenuButton
                as={Box}
                cursor={isDevMode ? 'not-allowed' : 'pointer'}
                opacity={isDevMode ? 0.4 : 1}
                _hover={{ opacity: isDevMode ? 0.4 : undefined }}
                onClick={
                  isDevMode
                    ? (e: React.MouseEvent) => e.preventDefault()
                    : undefined
                }
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap="4px"
                onMouseEnter={() => setHoveredAction('lang')}
                onMouseLeave={() => setHoveredAction(null)}
              >
                <CustomEarthIcon
                  color="#8E8E8E"
                  boxSize="24px"
                  gradient={
                    isNonDefaultLanguageSelected
                      ? 'selected'
                      : hoveredAction === 'lang' && !isDevMode
                        ? 'hover'
                        : undefined
                  }
                />
                <Text
                  fontSize="10px"
                  fontFamily="Inter SemiBold, sans-serif"
                  textAlign="center"
                  {...langTextProps}
                >
                  {isPassPreview
                    ? (
                        selectedPassLanguage ||
                        passDefaultLanguage ||
                        'en'
                      ).toUpperCase()
                    : (previewLanguage || defaultLanguage).toUpperCase()}
                </Text>
              </MenuButton>
            </Tooltip>
            <MenuList>
              {!isPassPreview ? (
                <>
                  <MenuItem
                    onClick={() => dispatch(setPreviewLanguage(null))}
                    fontWeight={
                      previewLanguage === null || previewLanguage === undefined
                        ? 'bold'
                        : 'normal'
                    }
                  >
                    {getLocaleDisplayName(defaultLanguage)} (Default)
                  </MenuItem>
                  {addedLanguages.map((lang) => (
                    <MenuItem
                      key={lang}
                      onClick={() => dispatch(setPreviewLanguage(lang))}
                      fontWeight={previewLanguage === lang ? 'bold' : 'normal'}
                    >
                      {getLocaleDisplayName(lang)}
                    </MenuItem>
                  ))}
                </>
              ) : (
                <>
                  <MenuItem
                    onClick={() => onPassLanguageChange?.(null)}
                    fontWeight={!selectedPassLanguage ? 'bold' : 'normal'}
                  >
                    {getLocaleDisplayName(passDefaultLanguage || 'en')}{' '}
                    (Default)
                  </MenuItem>
                  {passLanguages?.map((lang) => (
                    <MenuItem
                      key={lang}
                      onClick={() => onPassLanguageChange?.(lang)}
                      fontWeight={
                        selectedPassLanguage === lang ? 'bold' : 'normal'
                      }
                    >
                      {getLocaleDisplayName(lang)}
                    </MenuItem>
                  ))}
                </>
              )}
            </MenuList>
          </Menu>
        </>
      ) : null}

      <Box w="24px" h="1px" bg="lightGrey" opacity={0.7} />

      <Tooltip
        label={isDevMode ? 'Exit dev mode' : 'Dev mode'}
        {...TOOLTIP_PROPS}
      >
        <IconButton
          aria-label={isDevMode ? 'Exit dev mode' : 'Dev mode'}
          variant="ghost"
          size="xs"
          onClick={() => setIsDevMode(!isDevMode)}
          _hover={{ bg: 'transparent' }}
          onMouseEnter={() => setHoveredAction('devmode')}
          onMouseLeave={() => setHoveredAction(null)}
        >
          <CustomDevModeIcon
            color={isDevMode ? 'orangePrimary' : '#8E8E8E'}
            boxSize="24px"
            gradient={
              isDevMode
                ? 'selected'
                : hoveredAction === 'devmode'
                  ? 'hover'
                  : undefined
            }
          />
        </IconButton>
      </Tooltip>
    </Flex>
  );
};

export default PreviewActionsPill;
